import {
  ArrowClockwiseIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CoinsIcon,
  InfoIcon,
  SparkleIcon,
  TargetIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";

import { useLocaleStorage } from "@/client/hooks/use-locale-storage";
import { useAiWallet } from "@/client/services/ai-credit/api";
import { isAiInsufficientPointsError } from "@/client/services/ai/errors";
import { getAtsTarget, saveAtsTarget } from "@/client/services/ats/ats-target";
import { type AtsScoreResult, useAtsQuote, useScore } from "@/client/services/ollama/ats-score";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

const getErrorMessage = (error: unknown) => {
  const apiMessage = (
    error as {
      response?: {
        data?: {
          message?: string;
          error?: string;
        };
      };
    }
  )?.response?.data;

  return (
    apiMessage?.message ??
    apiMessage?.error ??
    (error as Error)?.message ??
    "Une erreur est survenue."
  );
};

const getScoreLabel = (score: number) => {
  if (score >= 80) {
    return {
      label: "Excellent potentiel",
      color: "#10b981",
    };
  }

  if (score >= 60) {
    return {
      label: "Bon potentiel",
      color: "#6366f1",
    };
  }

  return {
    label: "À améliorer",
    color: "#f59e0b",
  };
};

const ScoreCircle = ({ value }: { value: number }) => {
  const normalizedValue = Math.max(0, Math.min(100, value));

  const status = getScoreLabel(normalizedValue);

  return (
    <div
      className="relative grid size-28 shrink-0 place-items-center rounded-full"
      style={{
        background: `conic-gradient(${status.color} ${normalizedValue * 3.6}deg, #e9e7f7 0deg)`,
      }}
    >
      <div className="grid size-[92px] place-items-center rounded-full bg-white text-center">
        <div>
          <strong className="block text-3xl font-bold text-[#071451]">{normalizedValue}</strong>

          <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
            sur 100
          </span>
        </div>
      </div>
    </div>
  );
};

export const AtsPanel = () => {
  const resume = useResumeStore((state) => state.resume);

  const storageKey = `moncvpro:ats-result:${resume.id}`;

  const [storedResult, setStoredResult] = useLocaleStorage(storageKey, "");

  const score = useScore(resume.id);
  const quote = useAtsQuote();
  const wallet = useAiWallet();

  const { open: openAiPacks } = useDialog("ai-packs");

  const [onboardingTarget, setOnboardingTarget] = useState(() => getAtsTarget(resume.id));

  const [jobDescription, setJobDescription] = useState(
    () => onboardingTarget?.jobDescription ?? "",
  );

  const result = useMemo(() => {
    if (score.data) {
      return score.data;
    }

    if (storedResult && typeof storedResult === "object") {
      return storedResult as AtsScoreResult;
    }

    return null;
  }, [score.data, storedResult]);

  const onboardingDescription = onboardingTarget?.jobDescription.trim() ?? "";

  const usesOnboardingOffer =
    Boolean(onboardingDescription) && jobDescription.trim() === onboardingDescription;

  const total = Math.round(result?.ats_score ?? 0);

  const status = getScoreLabel(total);

  useEffect(() => {
    const target = getAtsTarget(resume.id);

    setOnboardingTarget(target);

    setJobDescription(target?.jobDescription ?? "");
  }, [resume.id]);

  useEffect(() => {
    if (score.data) {
      setStoredResult(score.data);
    }
  }, [score.data, setStoredResult]);

  const changeJobDescription = (value: string) => {
    setJobDescription(value);

    if (quote.data) {
      quote.reset();
    }
  };

  const saveCurrentTarget = () => {
    const current = getAtsTarget(resume.id);

    saveAtsTarget(resume.id, {
      targetJob: current?.targetJob ?? "",
      sector: current?.sector ?? "",
      applicationType: current?.applicationType ?? null,
      jobDescription,
      source: usesOnboardingOffer ? "onboarding" : "builder",
    });

    setOnboardingTarget(getAtsTarget(resume.id));
  };

  const requestQuote = () => {
    const description = jobDescription.trim();

    if (!description) {
      return;
    }

    saveCurrentTarget();

    quote.mutate({
      resume: resume.data as Record<string, unknown>,
      jobDescription: description,
    });
  };

  const openRecharge = () => {
    sessionStorage.setItem(`moncvpro:ats-resume-after-topup:${resume.id}`, "1");

    openAiPacks("create", {
      id: "ai-packs",
      item: {
        type: "scoring",
      },
    });
  };

  const runAnalysis = async () => {
    const description = jobDescription.trim();

    if (!description) {
      return;
    }

    saveCurrentTarget();

    try {
      await score.mutateAsync({
        resume: resume.data as Record<string, unknown>,
        jobdesc: description,
      });
    } catch (error) {
      if (isAiInsufficientPointsError(error)) {
        openRecharge();
      }
    }
  };

  const restart = () => {
    score.reset();
    quote.reset();
  };

  const pending = score.isPending || quote.isPending;

  const scoreFailed = score.isError && !isAiInsufficientPointsError(score.error);

  const currentBalance = wallet.data?.balance ?? quote.data?.balance ?? 0;

  const canAfford = Boolean(
    quote.data && (quote.data.affordable || currentBalance >= quote.data.points),
  );

  useEffect(() => {
    const resumeAfterTopup = sessionStorage.getItem(`moncvpro:ats-resume-after-topup:${resume.id}`);

    if (!resumeAfterTopup || !jobDescription.trim() || quote.isPending || quote.data) {
      return;
    }

    sessionStorage.removeItem(`moncvpro:ats-resume-after-topup:${resume.id}`);

    requestQuote();
  }, [resume.id, jobDescription, quote.isPending, quote.data]);

  return (
    <div
      className={[
        "h-full overflow-y-auto px-4 py-5",
        "[scrollbar-color:#818cf8_transparent]",
        "[scrollbar-width:thin]",
        "[&::-webkit-scrollbar]:w-1.5",
        "[&::-webkit-scrollbar-track]:bg-transparent",
        "[&::-webkit-scrollbar-thumb]:rounded-full",
        "[&::-webkit-scrollbar-thumb]:bg-indigo-300",
        "sm:px-5",
      ].join(" ")}
    >
      <header>
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600">
          Compatibilité
        </p>

        <h2 className="mt-1 text-xl font-bold tracking-tight text-[#071451]">Analyse ATS</h2>

        <p className="mt-1 max-w-xl text-xs leading-5 text-indigo-950/70">
          Comparez votre CV à une offre précise et découvrez les améliorations prioritaires.
        </p>
      </header>

      <section className="mt-4 rounded-2xl border border-indigo-100 bg-white p-4 shadow-[0_6px_20px_rgba(30,27,75,0.05)]">
        <div className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
            <TargetIcon size={19} weight="duotone" />
          </span>

          <div className="min-w-0">
            <h3 className="text-xs font-bold text-[#071451]">Offre d’emploi ciblée</h3>

            <p className="mt-0.5 text-[10px] leading-4 text-slate-500">
              L’analyse se base sur les missions, compétences et mots-clés de cette offre.
            </p>
          </div>
        </div>

        {usesOnboardingOffer && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2.5">
            <CheckCircleIcon size={17} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" />

            <div>
              <p className="text-[10px] font-bold text-emerald-800">
                Offre récupérée depuis l’onboarding
              </p>

              {onboardingTarget?.targetJob && (
                <p className="mt-0.5 text-[10px] text-emerald-700">
                  Poste ciblé : {onboardingTarget.targetJob}
                </p>
              )}
            </div>
          </div>
        )}

        <label className="mt-3 block text-[11px] font-bold text-[#071451]">
          Description du poste
          <textarea
            value={jobDescription}
            rows={5}
            maxLength={12_000}
            disabled={score.isPending}
            placeholder="Collez ici l’offre d’emploi : missions, compétences, expérience et qualifications recherchées…"
            onChange={(event) => {
              changeJobDescription(event.target.value);
            }}
            onBlur={saveCurrentTarget}
            className="mt-1.5 min-h-28 w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-medium leading-5 text-[#071451] outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50"
          />
        </label>

        <div className="mt-2 flex items-center justify-between gap-3 text-[9px] text-slate-400">
          <span>{jobDescription.trim().length} caractères</span>

          {!usesOnboardingOffer && jobDescription.trim() && (
            <span>Offre ajoutée dans le builder</span>
          )}
        </div>

        {!quote.data && !score.isPending && (
          <button
            type="button"
            disabled={!jobDescription.trim() || quote.isPending}
            onClick={requestQuote}
            className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 px-4 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
          >
            <CoinsIcon size={17} weight="duotone" />

            {quote.isPending ? "Estimation en cours…" : "Estimer le coût de l’analyse"}
          </button>
        )}

        {quote.isError && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[10px] font-medium leading-4 text-rose-700">
            <WarningCircleIcon size={16} className="mt-0.5 shrink-0" />

            {getErrorMessage(quote.error)}
          </div>
        )}

        {quote.data && !score.isPending && (
          <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-white p-2.5">
                <p className="text-[9px] font-semibold text-slate-400">Coût estimé</p>

                <strong className="mt-0.5 block text-sm text-[#071451]">
                  {quote.data.points} points
                </strong>
              </div>

              <div className="rounded-lg bg-white p-2.5">
                <p className="text-[9px] font-semibold text-slate-400">Votre solde</p>

                <strong className="mt-0.5 block text-sm text-[#071451]">
                  {currentBalance} points
                </strong>
              </div>
            </div>

            <p className="mt-2 flex items-start gap-1.5 text-[9px] leading-4 text-indigo-700">
              <InfoIcon size={14} className="mt-0.5 shrink-0" />
              Le coût final sera ajusté selon les tokens réellement consommés.
            </p>

            <button
              type="button"
              disabled={score.isPending}
              onClick={() => {
                if (canAfford) {
                  void runAnalysis();
                  return;
                }

                openRecharge();
              }}
              className={[
                "mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-xs font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-40",
                canAfford
                  ? "bg-indigo-700 hover:bg-indigo-800"
                  : "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 hover:-translate-y-0.5",
              ].join(" ")}
            >
              {canAfford ? (
                <ChartBarIcon size={16} weight="bold" />
              ) : (
                <CoinsIcon size={16} weight="duotone" />
              )}

              {canAfford ? "Confirmer et analyser mon CV" : "Recharger mes crédits IA"}
            </button>

            {!canAfford && (
              <p className="mt-2 text-center text-[9px] leading-4 text-slate-500">
                Il vous manque {Math.max(0, quote.data.points - currentBalance).toFixed(2)} points
                pour lancer cette analyse.
              </p>
            )}
          </div>
        )}
      </section>

      {score.isPending && (
        <section className="mt-4 overflow-hidden rounded-2xl border border-indigo-100 bg-white p-4">
          <div className="flex items-center gap-3">
            <span className="relative grid size-10 place-items-center rounded-full bg-indigo-50">
              <span className="absolute inset-0 animate-ping rounded-full bg-indigo-200/60" />

              <SparkleIcon size={18} weight="duotone" className="relative text-indigo-600" />
            </span>

            <div>
              <h3 className="text-xs font-bold text-[#071451]">Analyse en cours</h3>

              <p className="mt-0.5 text-[10px] leading-4 text-slate-500">
                Nous comparons chaque section du CV aux exigences du poste.
              </p>
            </div>
          </div>

          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-indigo-50">
            <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-indigo-600 to-emerald-400" />
          </div>
        </section>
      )}

      {scoreFailed && (
        <section className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <div className="flex items-start gap-2">
            <WarningCircleIcon size={18} className="mt-0.5 shrink-0 text-rose-600" />

            <div>
              <h3 className="text-xs font-bold text-rose-800">Analyse impossible</h3>

              <p className="mt-1 text-[10px] leading-4 text-rose-700">
                {getErrorMessage(score.error)}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={restart}
            className="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-[10px] font-bold text-rose-700"
          >
            <ArrowClockwiseIcon size={14} />
            Réessayer
          </button>
        </section>
      )}

      {result && total > 0 && !score.isPending && (
        <section className="mt-4 rounded-2xl border border-indigo-100 bg-white p-4 shadow-[0_6px_20px_rgba(30,27,75,0.05)]">
          <div className="xs:flex-row flex flex-col items-center gap-4">
            <ScoreCircle value={total} />

            <div className="xs:text-left min-w-0 text-center">
              <h3 className="text-base font-bold text-[#071451]">{status.label}</h3>

              <p className="mt-1 text-[10px] leading-4 text-slate-500">
                {result.summary ||
                  "Appliquez les recommandations prioritaires pour renforcer la compatibilité de votre CV."}
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-center">
              <p className="text-[9px] font-semibold text-slate-400">Compétences</p>

              <strong className="mt-1 block text-lg text-indigo-600">
                {Math.round(result.skills_match_percentage ?? 0)}%
              </strong>
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-center">
              <p className="text-[9px] font-semibold text-slate-400">Expérience</p>

              <strong className="mt-1 block text-lg text-emerald-500">
                {Math.round(result.experience_relevance ?? 0)}%
              </strong>
            </div>
          </div>

          {!!result.missing_keywords?.length && (
            <div className="mt-4">
              <h4 className="text-[11px] font-bold text-[#071451]">Mots-clés manquants</h4>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {result.missing_keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-[9px] font-semibold text-indigo-700"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!!result.improvement_suggestions?.length && (
            <div className="mt-4">
              <h4 className="text-[11px] font-bold text-[#071451]">Priorités recommandées</h4>

              <ul className="mt-2 space-y-2">
                {result.improvement_suggestions.slice(0, 5).map((suggestion, index) => (
                  <li
                    key={`${suggestion.suggestion}-${index}`}
                    className="flex gap-2 rounded-xl bg-slate-50 p-2.5 text-[10px] leading-4 text-[#071451]"
                  >
                    <SparkleIcon
                      size={15}
                      weight="duotone"
                      className="mt-0.5 shrink-0 text-emerald-500"
                    />

                    {suggestion.suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="button"
            disabled={pending}
            onClick={() => {
              quote.reset();
            }}
            className="mx-auto mt-4 flex items-center gap-2 text-[10px] font-bold text-indigo-600 hover:text-indigo-800"
          >
            <ArrowClockwiseIcon size={14} />
            Relancer l’analyse
          </button>
        </section>
      )}
    </div>
  );
};
